import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { PageHeader, Loader, Empty } from "@/components/shared";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { fmtDate } from "@/lib/format";

const COLORS = ["#0B2455", "#C9A227", "#1F4690", "#A8841A", "#0B2455", "#C9A227", "#1F4690", "#A8841A"];

export default function Spin() {
  const { t } = useI18n();
  const { setUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const wheelRef = useRef(null);

  const load = () => {
    api.get("/spin/status").then(({ data }) => setStatus(data)).catch(() => {});
    api.get("/spin/history").then(({ data }) => setHistory(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (!status) return <Loader />;

  const segments = status.segments || [];
  const seg = 360 / (segments.length || 8);

  const doSpin = async () => {
    if (spinning || !status.can_spin) return;
    setSpinning(true); setResult(null);
    try {
      const { data } = await api.post("/spin");
      const idx = data.segment_index;
      // land pointer (top) at the middle of the winning segment
      const target = 360 * 6 + (360 - (idx * seg + seg / 2));
      setRotation((r) => r - (r % 360) + target);
      setTimeout(async () => {
        setResult(data);
        if (data.points > 0) toast.success(`You won ${data.points} points (Rs ${data.pkr})!`);
        else toast("Try Again tomorrow!");
        const me = await api.get("/auth/me"); setUser(me.data);
        load(); setSpinning(false);
      }, 4200);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setSpinning(false);
    }
  };

  return (
    <div>
      <PageHeader title={t("daily_spin")} subtitle="One free spin every calendar day. Resets at 12:00 AM." />
      <div className="grid lg:grid-cols-2 gap-8 items-start">
        <div className="ec-card p-8 flex flex-col items-center">
          <div className="relative w-72 h-72 sm:w-80 sm:h-80">
            <div className="absolute top-[-14px] left-1/2 -translate-x-1/2 z-10 w-0 h-0 border-l-[14px] border-r-[14px] border-t-[26px] border-l-transparent border-r-transparent border-t-orange-500 drop-shadow-lg" />
            <div ref={wheelRef} className="w-full h-full rounded-full border-4 border-orange-500/40 overflow-hidden transition-transform duration-[4000ms] ease-out"
              style={{ transform: `rotate(${rotation}deg)`, background: `conic-gradient(${segments.map((_, i) => `${COLORS[i % COLORS.length]} ${i * seg}deg ${(i + 1) * seg}deg`).join(",")})` }}
              data-testid="spin-wheel">
              {segments.map((s, i) => {
                const angle = i * seg + seg / 2;
                return (
                  <div key={i} className="absolute top-1/2 left-1/2 origin-left text-[11px] font-bold text-pure keep-ltr drop-shadow"
                    style={{ transform: `rotate(${angle}deg) translate(58px, -6px)` }}>
                    {s.label}
                  </div>
                );
              })}
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-pure border-4 border-yellow-500 flex items-center justify-center font-head font-extrabold text-orange-400 text-xs">GO</div>
          </div>
          <button onClick={doSpin} disabled={spinning || !status.can_spin} data-testid="spin-wheel-trigger"
            className="mt-8 px-10 py-3 rounded-full bg-orange-600 hover:bg-orange-500 text-pure disabled:opacity-40 btn-glow font-head font-bold text-lg">
            {spinning ? "Spinning..." : status.can_spin ? t("spin_now") : "Come back tomorrow"}
          </button>
          {!status.can_spin && <p className="text-sm text-gray-500 mt-3" data-testid="spin-used-msg">{t("spin_used")}</p>}
          {result && <p className="mt-4 font-head font-bold text-gradient text-lg" data-testid="spin-result">{result.try_again ? "Try Again!" : `+${result.points} points`}</p>}
        </div>

        <div className="ec-card p-6">
          <h3 className="font-head text-lg font-semibold text-white mb-4">{t("spin_history")}</h3>
          {history.length === 0 ? <p className="text-gray-500 text-sm py-8 text-center">No spins yet.</p> : (
            <div className="divide-y divide-white/5 max-h-96 overflow-y-auto">
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between py-3" data-testid={`spin-hist-${h.id}`}>
                  <div><p className="text-sm text-white">{h.label}</p><p className="text-xs text-gray-500">{fmtDate(h.created_at)}</p></div>
                  <span className={`font-head font-bold ${h.points > 0 ? "text-emerald-400" : "text-gray-500"}`}>{h.points > 0 ? `+${h.points}` : "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
